import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useCreateClassifierDrawer } from './useCreateClassifierDrawer';
import { EditField } from '@dealstack/frontend/shared/components/Documents/FormFields/EditField';
import { CreateDealClassifierDto, DealClassifierTct } from '@dealstack/shared/lib/generated';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Classifier } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/types';
import { DrawerSubmission } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/DrawerSubmission';
import {
	ClassifierForm,
	DrawFieldContainer,
	FieldWrapper,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/styled';
import {
	nameField,
	parentIdField,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/fields';
import { createClassifiersOptions } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils';

interface Props {
	classifier: Classifier;
	handleOnCancel: () => void;
	availableClassifiers?: DealClassifierTct[];
	setShowCreateUpdateClassificationDrawer: Dispatch<SetStateAction<boolean>>;
}

export const DrawerBodyCreate = ({
	classifier,
	handleOnCancel,
	availableClassifiers,
	setShowCreateUpdateClassificationDrawer,
}: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { formState, onSave, isLoading } = useCreateClassifierDrawer(
		classifier,
		setShowCreateUpdateClassificationDrawer
	);

	const options = useMemo(
		() => createClassifiersOptions(availableClassifiers),
		[availableClassifiers]
	);

	const formFields = useMemo(
		() => [nameField(tAdminDashboard), parentIdField(tAdminDashboard, options)],
		[tAdminDashboard, options]
	);

	const onCancel = useCallback(() => {
		formState.reset();
		handleOnCancel();
	}, [handleOnCancel, formState]);

	return (
		<DrawFieldContainer>
			<FormProvider<CreateDealClassifierDto> {...formState}>
				<ClassifierForm>
					{formFields.map((field) => (
						<FieldWrapper key={`${field.fieldKey}_${field.label}`}>
							<EditField field={field} control={formState.control} />
						</FieldWrapper>
					))}

					{isLoading ? (
						<InlineLoader />
					) : (
						<DrawerSubmission onCancel={onCancel} onSave={onSave} />
					)}
				</ClassifierForm>
			</FormProvider>
		</DrawFieldContainer>
	);
};
